/**
 * Comment-category taxonomy for the code-review trend charts. Slugs must
 * match backend/pr_reviewer/comment_taxonomy.py exactly.
 *
 * Colors: a brand-green family (olive → sage → mint) plus slate/gray for the
 * tail, shaded so the highest-volume categories carry the darkest greens and
 * the stack reads dark-at-the-bottom. Color follows the CATEGORY, never its
 * rank — a repo/date filter that reorders the series must not repaint them.
 * The green steps are CSS vars so they auto-flip (the darkest olive would be
 * invisible on the dark surface); the mint/slate/gray steps are mid-tone and
 * fixed, same convention as SERIES_COLORS (design/tokens.md §7b).
 */

export interface CommentCategoryMeta {
  slug: string;
  label: string;
  /** Legend/chip form — full labels are too long for a single-row legend. */
  short: string;
  color: string;
}

export const COMMENT_CATEGORIES: CommentCategoryMeta[] = [
  {
    slug: 'logic_correctness_bugs',
    label: 'Logic/Correctness Bugs',
    short: 'Logic / Correctness',
    color: 'var(--primitive-brand-green-800)',
  },
  {
    slug: 'null_missing_value_crashes',
    label: 'Null/Missing Value Crashes',
    short: 'Null / Missing Value',
    color: '#A9E8BC',
  },
  {
    slug: 'error_handling_silent_failures',
    label: 'Error Handling/Silent Failures',
    short: 'Error Handling',
    color: 'var(--primitive-brand-green-700)',
  },
  {
    slug: 'stale_state_react_hooks',
    label: 'Stale State/React Hooks',
    short: 'Stale State',
    color: 'var(--primitive-brand-green-600)',
  },
  {
    slug: 'dead_code_maintainability',
    label: 'Dead Code/Maintainability',
    short: 'Dead Code',
    color: '#8A93A6',
  },
  {
    slug: 'security',
    label: 'Security',
    short: 'Security',
    color: 'var(--primitive-brand-green-500)',
  },
  {
    slug: 'test_correctness',
    label: 'Test Correctness',
    short: 'Test Correctness',
    color: '#C3D9A0',
  },
  {
    slug: 'logging_violations',
    label: 'Logging Violations',
    short: 'Logging',
    color: '#A9A9A9',
  },
  {
    slug: 'design_system_violations',
    label: 'Design System Violations',
    short: 'Design System',
    color: '#6F8FA8',
  },
  {
    slug: 'cross_platform_config',
    label: 'Cross-Platform/Config',
    short: 'Cross-Platform',
    color: '#CFCFCF',
  },
  {
    slug: 'performance',
    label: 'Performance',
    short: 'Performance',
    color: '#8FBF9F',
  },
];

export const COMMENT_CATEGORY_BY_SLUG: Record<string, CommentCategoryMeta> =
  Object.fromEntries(COMMENT_CATEGORIES.map((c) => [c.slug, c]));

export function commentCategoryLabel(slug: string): string {
  return COMMENT_CATEGORY_BY_SLUG[slug]?.label ?? slug;
}

export function commentCategoryShortLabel(slug: string): string {
  return COMMENT_CATEGORY_BY_SLUG[slug]?.short ?? slug;
}

export function commentCategoryColor(slug: string): string {
  return COMMENT_CATEGORY_BY_SLUG[slug]?.color ?? 'var(--primitive-gray-500)';
}
